import React, { useState } from 'react';
import { X, Upload, FileText, Download, Copy, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SHADOWS } from '../../styles/emziCI';
import { generateWhatsAppLink, getNewUserMessageTemplate, copyToClipboard, generateCreatedUsersCSV, downloadCSV } from '../../utils/whatsapp';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Bulk Upload Modal
 * Purpose: Upload CSV/XLSX to create multiple users at once
 */

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const t = useLang();
  const [step, setStep] = useState(1); // 1: template, 2: upload, 3: preview, 4: result
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  
  const handleClose = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setResult(null);
    setError('');
    onClose();
  };

  const downloadTemplate = async (format) => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(`${BACKEND_URL}/api/admin-panel/users/bulk/template?fmt=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `emzi_nexus_users_template.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Template download error:', err);
    }
  };

  const handleFileSelect = (selectedFile) => {
    setError('');
    
    // Validate file type
    const validTypes = ['.csv', '.xlsx'];
    const fileExt = selectedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    
    if (!validTypes.includes(fileExt)) {
      setError(t('admin.users.bulk.invalidFileType'));
      return;
    }

    // Validate file size (2MB)
    if (selectedFile.size > 2 * 1024 * 1024) {
      setError(t('admin.users.bulk.fileTooLarge'));
      return;
    }

    setFile(selectedFile);
    setStep(2);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleUploadAndPreview = async () => {
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/admin-panel/users/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        setStep(4);
        
        // Show success toast
        const createdCount = data.stats.created || 0;
        const skippedCount = data.stats.skipped || 0;
        
        if (createdCount > 0) {
          toast.success(t('admin.users.success.bulkCreated').replace('{count}', createdCount));
        }
        if (skippedCount > 0) {
          toast.warning(`${skippedCount} users were skipped due to errors`);
        }
        
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        const errorMsg = data.detail || t('admin.users.bulk.uploadFailed');
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(t('admin.users.bulk.uploadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = (user) => {
    const loginUrl = window.location.origin + '/login';
    const message = getNewUserMessageTemplate(
      user.email,
      user.temp_password,
      user.full_name,
      loginUrl
    );
    const waLink = generateWhatsAppLink(user.phone_e164, message);
    window.open(waLink, '_blank');
  };

  const handleCopyPassword = async (password) => {
    const success = await copyToClipboard(password);
    if (success) {
      toast.success(t('admin.users.passwordCopied'));
    }
  };

  const handleDownloadCreatedCSV = () => {
    if (!result || !result.created) return;
    
    const csv = generateCreatedUsersCSV(result.created);
    downloadCSV(csv, `emzi_nexus_created_users_${Date.now()}.csv`);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: EMZI_SHADOWS.xl
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: EMZI_COLORS.textPrimary,
            margin: 0
          }}>
            {t('admin.users.bulk.title')}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} color={EMZI_COLORS.textSecondary} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {/* Step 1: Download Template */}
          {step === 1 && (
            <div>
              <p style={{
                fontSize: '14px',
                color: EMZI_COLORS.textSecondary,
                marginBottom: '24px'
              }}>
                {t('admin.users.bulk.downloadInstructions')}
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <button
                  onClick={() => downloadTemplate('csv')}
                  style={{
                    padding: '16px',
                    background: EMZI_COLORS.surface,
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FileText size={32} color={EMZI_COLORS.primary} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textPrimary
                  }}>
                    {t('admin.users.bulk.downloadTemplateCsv')}
                  </span>
                </button>

                <button
                  onClick={() => downloadTemplate('xlsx')}
                  style={{
                    padding: '16px',
                    background: EMZI_COLORS.surface,
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FileText size={32} color={EMZI_COLORS.success} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textPrimary
                  }}>
                    {t('admin.users.bulk.downloadTemplateXlsx')}
                  </span>
                </button>
              </div>

              <button
                onClick={() => setStep(2)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: EMZI_COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('admin.users.bulk.nextUpload')}
              </button>
            </div>
          )}

          {/* Step 2: Upload File */}
          {step === 2 && (
            <div>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${isDragging ? EMZI_COLORS.primary : EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  padding: '40px 20px',
                  textAlign: 'center',
                  marginBottom: '16px',
                  background: isDragging ? EMZI_COLORS.surface : 'transparent'
                }}
              >
                <Upload size={48} color={EMZI_COLORS.textSecondary} style={{ margin: '0 auto 16px' }} />
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.users.bulk.dragDropOrClick')}
                </p>
                <p style={{
                  fontSize: '14px',
                  color: EMZI_COLORS.textSecondary,
                  marginBottom: '16px'
                }}>
                  CSV or XLSX (max 2MB)
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    background: EMZI_COLORS.primary,
                    color: 'white',
                    borderRadius: EMZI_RADIUS.sm,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {t('admin.users.bulk.selectFile')}
                </label>
              </div>

              {file && (
                <div style={{
                  padding: '16px',
                  background: EMZI_COLORS.surface,
                  borderRadius: EMZI_RADIUS.md,
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FileText size={24} color={EMZI_COLORS.primary} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: EMZI_COLORS.textPrimary
                      }}>
                        {file.name}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: EMZI_COLORS.textSecondary
                      }}>
                        {(file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      <X size={20} color={EMZI_COLORS.textSecondary} />
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div style={{
                  padding: '12px',
                  background: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: EMZI_RADIUS.md,
                  color: '#991b1b',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setStep(1)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'white',
                    color: EMZI_COLORS.textPrimary,
                    border: `1px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={handleUploadAndPreview}
                  disabled={!file || loading}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: file && !loading ? EMZI_COLORS.primary : EMZI_COLORS.textSecondary,
                    color: 'white',
                    border: 'none',
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: file && !loading ? 'pointer' : 'not-allowed'
                  }}
                >
                  {loading ? t('admin.users.bulk.importing') : t('admin.users.bulk.import')}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && result && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px',
                  background: '#ecfdf5',
                  borderRadius: EMZI_RADIUS.md,
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#059669'
                  }}>
                    {result.stats.created}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#047857',
                    marginTop: '4px'
                  }}>
                    {t('admin.users.bulk.createdCount')}
                  </div>
                </div>

                <div style={{
                  padding: '16px',
                  background: '#fef3c7',
                  borderRadius: EMZI_RADIUS.md,
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#d97706'
                  }}>
                    {result.stats.skipped}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#b45309',
                    marginTop: '4px'
                  }}>
                    {t('admin.users.bulk.skippedCount')}
                  </div>
                </div>
              </div>

              {result.created.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: EMZI_COLORS.textPrimary,
                      margin: 0
                    }}>
                      {t('admin.users.bulk.createdUsers')}
                    </h3>
                    <button
                      onClick={handleDownloadCreatedCSV}
                      style={{
                        padding: '8px 16px',
                        background: EMZI_COLORS.success,
                        color: 'white',
                        border: 'none',
                        borderRadius: EMZI_RADIUS.sm,
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Download size={16} />
                      {t('admin.users.bulk.exportCreatedCsv')}
                    </button>
                  </div>

                  <div style={{
                    maxHeight: '400px',
                    overflow: 'auto',
                    border: `1px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md
                  }}>
                    {result.created.map((user, idx) => (
                      <div
                        key={`item-${idx}`}
                        style={{
                          padding: '16px',
                          borderBottom: idx < result.created.length - 1 ? `1px solid ${EMZI_COLORS.border}` : 'none'
                        }}
                      >
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: EMZI_COLORS.textPrimary,
                          marginBottom: '8px'
                        }}>
                          {user.email}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: EMZI_COLORS.textSecondary,
                          marginBottom: '12px'
                        }}>
                          {user.full_name} • {user.role} • {user.phone_e164}
                        </div>

                        <div style={{
                          padding: '12px',
                          background: '#fef3c7',
                          borderRadius: EMZI_RADIUS.sm,
                          marginBottom: '8px'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#92400e',
                            marginBottom: '4px'
                          }}>
                            {t('admin.users.tempPasswordLabel')}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code style={{
                              flex: 1,
                              padding: '6px 8px',
                              background: 'white',
                              borderRadius: EMZI_RADIUS.sm,
                              fontSize: '13px',
                              fontFamily: 'monospace',
                              color: '#92400e'
                            }}>
                              {user.temp_password}
                            </code>
                            <button
                              onClick={() => handleCopyPassword(user.temp_password)}
                              style={{
                                padding: '6px 10px',
                                background: '#fbbf24',
                                color: '#92400e',
                                border: 'none',
                                borderRadius: EMZI_RADIUS.sm,
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              <Copy size={14} />
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={() => handleShareWhatsApp(user)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <MessageCircle size={16} />
                          {t('admin.users.shareViaWhatsapp')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.skipped.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textPrimary,
                    marginBottom: '12px'
                  }}>
                    {t('admin.users.bulk.skippedRows')}
                  </h3>
                  <div style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    border: `1px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md
                  }}>
                    {result.skipped.map((skip, idx) => (
                      <div
                        key={`item-${idx}`}
                        style={{
                          padding: '12px',
                          borderBottom: idx < result.skipped.length - 1 ? `1px solid ${EMZI_COLORS.border}` : 'none',
                          display: 'flex',
                          alignItems: 'start',
                          gap: '8px'
                        }}
                      >
                        <AlertCircle size={16} color="#ef4444" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: EMZI_COLORS.textPrimary
                          }}>
                            Row {skip.row}: {skip.email}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#991b1b',
                            marginTop: '2px'
                          }}>
                            {skip.reason}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleClose}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: EMZI_COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('common.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
